import { itemDef } from "./items";
import { fuelValue, smeltOf } from "./crafting";
import { makeSlot, type SlotCell } from "./inventory";

export interface ChestState {
  slots: SlotCell[];
}

export interface FurnaceState {
  input: SlotCell;
  fuel: SlotCell;
  output: SlotCell;
  burn: number;
  cook: number;
}

function posKey(x: number, y: number, z: number): string {
  return `${x},${y},${z}`;
}

export class Containers {
  chests = new Map<string, ChestState>();
  furnaces = new Map<string, FurnaceState>();

  chest(x: number, y: number, z: number): ChestState {
    const k = posKey(x, y, z);
    let c = this.chests.get(k);
    if (!c) {
      c = { slots: Array.from({ length: 27 }, () => null) };
      this.chests.set(k, c);
    }
    return c;
  }

  furnace(x: number, y: number, z: number): FurnaceState {
    const k = posKey(x, y, z);
    let f = this.furnaces.get(k);
    if (!f) {
      f = { input: null, fuel: null, output: null, burn: 0, cook: 0 };
      this.furnaces.set(k, f);
    }
    return f;
  }

  remove(x: number, y: number, z: number): SlotCell[] {
    const k = posKey(x, y, z);
    const drops: SlotCell[] = [];
    const c = this.chests.get(k);
    if (c) {
      drops.push(...c.slots);
      this.chests.delete(k);
    }
    const f = this.furnaces.get(k);
    if (f) {
      drops.push(f.input, f.fuel, f.output);
      this.furnaces.delete(k);
    }
    return drops.filter(Boolean);
  }

  tick(dt: number): void {
    for (const f of this.furnaces.values()) this.tickFurnace(f, dt);
  }

  private tickFurnace(f: FurnaceState, dt: number): void {
    const recipe = f.input ? smeltOf(f.input.id) : undefined;
    if (f.burn > 0) f.burn -= dt;

    if (!recipe) {
      f.cook = 0;
      return;
    }
    if (f.output && (f.output.id !== recipe.output || f.output.count >= itemDef(recipe.output).stack)) {
      return;
    }

    if (f.burn <= 0) {
      if (f.fuel && fuelValue(f.fuel.id) > 0) {
        f.burn = fuelValue(f.fuel.id);
        f.fuel.count -= 1;
        if (f.fuel.count <= 0) f.fuel = null;
      } else {
        f.cook = Math.max(0, f.cook - dt * 2);
        return;
      }
    }

    f.cook += dt;
    if (f.cook >= recipe.time) {
      f.cook = 0;
      if (!f.input) return;
      f.input.count -= 1;
      if (f.input.count <= 0) f.input = null;
      if (!f.output) f.output = makeSlot(recipe.output, 1);
      else f.output.count += 1;
    }
  }

  serialize(): unknown {
    return {
      chests: [...this.chests.entries()],
      furnaces: [...this.furnaces.entries()],
    };
  }

  load(data: { chests?: [string, ChestState][]; furnaces?: [string, FurnaceState][] }): void {
    this.chests = new Map(data.chests ?? []);
    this.furnaces = new Map(data.furnaces ?? []);
  }
}
